'use strict';

/**
 * B2/B4 JIT custody — the browser-side SIGN-ONLY session signer.
 *
 * THE CUSTODY DECISION (B4 LD-8 option (b), browser topology — see
 * desktop/lib/sign-helper.js for the committed desktop twin of this pattern):
 * the JIT-grant signing capability is a NON-EXTRACTABLE WebCrypto Ed25519
 * CryptoKey derived from the master key at the ONE moment it is legitimately
 * present in a page (login unlock / T576 reload re-unlock), then held in the
 * in-memory vault session for the page lifetime. Raw key bytes exist only at
 * the import boundary and are zeroized there; past it, the only residue is a
 * handle no API can read back (subtle.exportKey refuses).
 *
 * WHY THIS SHAPE (doctrine, not taste):
 *   - B1 LD-2: the signing key is "derived/held inside the user-key boundary
 *     (browser / a sign-only helper)". The server NEVER sees it (Legal
 *     Foundation — custodian of ciphertext).
 *   - Blast-radius: the seed derives under the IDENTITY HKDF label
 *     ("oyster:identity:ed25519:v1"); content DEKs derive under the DATA label
 *     ("oyster:master-key-data:v1:..."). Total compromise of this signer can
 *     mint grants — single-use, 15-min, identity-bound, ledger-logged — but
 *     can NEVER decrypt content. Holding the master key warm instead would
 *     hand an XSS both capabilities forever; this hands it neither.
 *   - T213 posture: the capability dies with the page. NO IndexedDB, NO
 *     storage of the handle — cross-page travel is ONLY the T529 one-time
 *     E2E handoff carrying the derived seed (never the master key), imported
 *     + zeroized on arrival (deriveJitSigningSeed → createJitSigner({seed})).
 *
 * FALLBACK: engines without WebCrypto Ed25519 (feature-detected per call, not
 * assumed) get a closure-held seed signer — same scope (sign-only, master-
 * derived, page-lifetime), weaker extraction resistance (heap bytes), which
 * destroy() zeroizes. Baseline browsers as of 2026 all take the WebCrypto path.
 *
 * Browser + Node compatible (@noble for derivation, injectable `subtle`).
 */

const { deriveIdentityKeypair, signWithIdentity, zeroizeKey } = require('./identity-key.js');
const { ed25519 } = require('@noble/curves/ed25519.js');

// PKCS#8 DER prefix for an Ed25519 private key (RFC 8410): raw seed is the
// last 32 bytes. Identical constant to desktop/lib/sign-helper.js.
const PKCS8_ED25519_PREFIX = new Uint8Array([
  0x30, 0x2e, 0x02, 0x01, 0x00, 0x30, 0x05, 0x06,
  0x03, 0x2b, 0x65, 0x70, 0x04, 0x22, 0x04, 0x20,
]);

function typedError(code, message) {
  const err = new Error(message);
  err.code = code;
  return err;
}

/**
 * Derive the 32-byte sign-only seed from a master key — the ONLY form of the
 * capability allowed to ride the T529 login handoff (the master key itself
 * never travels to content pages). The caller's masterKey is untouched.
 *
 * @param {Uint8Array|Buffer} masterKey — 32 bytes
 * @returns {Uint8Array} fresh 32-byte Ed25519 seed (transfer to createJitSigner)
 */
function deriveJitSigningSeed(masterKey) {
  return deriveIdentityKeypair(masterKey).privateKey;
}

/**
 * Create the sign-only session signer. Exactly ONE of masterKey / seed:
 *   - masterKey: 32-byte Uint8Array — borrowed; the seed is derived here and
 *                zeroized after import. The caller's masterKey is untouched.
 *   - seed:      32-byte Ed25519 seed (deriveJitSigningSeed / handoff form) —
 *                TRANSFER semantics: the caller's buffer is ZEROIZED here, in
 *                every mode, so the seed cannot linger at the call site.
 *
 * @param {object} opts
 * @param {Uint8Array} [opts.masterKey]
 * @param {Uint8Array} [opts.seed]
 * @param {SubtleCrypto|null} [opts.subtle] — injectable; defaults to
 *   globalThis.crypto.subtle. Pass null to force the closure fallback (tests).
 * @returns {Promise<{ mode: 'webcrypto'|'closure', publicKey: Uint8Array,
 *   sign: (bytes: Uint8Array) => Promise<Uint8Array>, destroy: () => void,
 *   privateKeyHandle?: CryptoKey }>}
 */
async function createJitSigner({ masterKey, seed, subtle } = {}) {
  if (masterKey != null && seed != null) {
    throw typedError('ERR_JIT_SIGNER_KEY_MATERIAL_AMBIGUOUS',
      'createJitSigner: pass masterKey OR seed, not both');
  }
  if (masterKey == null && seed == null) {
    throw typedError('ERR_JIT_SIGNER_KEY_MATERIAL_REQUIRED',
      'createJitSigner: key material required — masterKey (32-byte Uint8Array) or seed');
  }

  let localSeed;
  if (masterKey != null) {
    // Fresh HKDF output — ours to zeroize; the caller's masterKey is untouched.
    localSeed = deriveIdentityKeypair(masterKey).privateKey;
  } else {
    if (!(seed instanceof Uint8Array) || seed.length !== 32) {
      throw typedError('ERR_JIT_SIGNER_SEED_INVALID',
        'createJitSigner: seed must be a 32-byte Uint8Array');
    }
    localSeed = new Uint8Array(seed);
    zeroizeKey(seed); // transfer: the caller's buffer must not outlive the import
  }

  // Public counterpart, computed BEFORE any zeroization.
  const publicKey = ed25519.getPublicKey(localSeed);

  const subtleImpl = subtle !== undefined
    ? subtle
    : ((typeof globalThis !== 'undefined' && globalThis.crypto && globalThis.crypto.subtle) || null);

  // Preferred path: non-extractable WebCrypto CryptoKey (feature-detected).
  if (subtleImpl && typeof subtleImpl.importKey === 'function') {
    const pkcs8 = new Uint8Array(PKCS8_ED25519_PREFIX.length + 32);
    pkcs8.set(PKCS8_ED25519_PREFIX, 0);
    pkcs8.set(localSeed, PKCS8_ED25519_PREFIX.length);
    try {
      const handle = await subtleImpl.importKey(
        'pkcs8', pkcs8, { name: 'Ed25519' }, /* extractable */ false, ['sign']);
      // Past this boundary the only residue is the non-extractable handle.
      zeroizeKey(pkcs8, localSeed);
      let destroyed = false;
      return {
        mode: 'webcrypto',
        publicKey,
        privateKeyHandle: handle,
        async sign(bytes) {
          if (destroyed) throw typedError('ERR_JIT_SIGNER_DESTROYED', 'jit-signer destroyed');
          const sig = await subtleImpl.sign({ name: 'Ed25519' }, handle, bytes);
          return new Uint8Array(sig);
        },
        destroy() { destroyed = true; }, // handle is unreadable; dropping use is all JS can do
      };
    } catch (_e) {
      // Engine lacks WebCrypto Ed25519 → closure fallback below.
      zeroizeKey(pkcs8);
    }
  }

  // Fallback: closure-held seed (sign-only scope unchanged; zeroized on destroy).
  let destroyed = false;
  return {
    mode: 'closure',
    publicKey,
    async sign(bytes) {
      if (destroyed) throw typedError('ERR_JIT_SIGNER_DESTROYED', 'jit-signer destroyed');
      return signWithIdentity(localSeed, bytes);
    },
    destroy() { destroyed = true; zeroizeKey(localSeed); },
  };
}

module.exports = { createJitSigner, deriveJitSigningSeed, PKCS8_ED25519_PREFIX };
